import { AuthHeader, AuthLayout, Button, type IconName } from "@nabble/ui";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { isSignedIn } from "../../../auth/authStateTransitions";
import { PRIVACY_PATH, TERMS_PATH, usePrivacyContact } from "../../../legal/legal";
import { PrivacyNoticeText } from "../../../legal/PrivacyNoticeText";
import { TermsOfUseText } from "../../../legal/TermsOfUseText";

export type LegalDocument = "privacy" | "terms";

interface LegalDocumentPage {
  title: string;
  icon: IconName;
  lead: string;
  /** The link to the other legal document, under the text. */
  other: { label: string; path: string };
}

const PAGES: Record<LegalDocument, LegalDocumentPage> = {
  privacy: {
    title: "Privacy notice",
    icon: "shield",
    lead: "How your personal data is handled when you use Nabble Suite.",
    other: { label: "Terms of use", path: TERMS_PATH },
  },
  terms: {
    title: "Terms of use",
    icon: "book",
    lead: "The rules for using Nabble Suite.",
    other: { label: "Privacy notice", path: PRIVACY_PATH },
  },
};

/** Public page with the privacy notice or the terms of use, readable signed in or not. */
export default function LegalPage({ document: doc }: { document: LegalDocument }): React.JSX.Element {
  const navigate = useNavigate();
  const { state } = useAuth();
  const contact = usePrivacyContact();
  const page = PAGES[doc];
  useDocumentTitle(page.title);

  function back(): void {
    // Came from somewhere in the suite: go back there. Opened directly (or in a new tab): go home.
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(isSignedIn(state) ? "/" : "/login");
  }

  return (
    <AuthLayout wide>
      <div>
        <Button label="Back" variant="ghost" size="sm" icon="arrowLeft" onClick={back} />
      </div>
      <AuthHeader icon={page.icon} title={page.title} lead={page.lead} />
      <div className="nb-auth-content">
        <article className="nb-doc" style={{ maxHeight: "none" }} aria-label={page.title}>
          {doc === "privacy" ? <PrivacyNoticeText contact={contact} /> : <TermsOfUseText />}
        </article>
        <p className="nb-small nb-subtle" style={{ textAlign: "center" }}>
          <Link to={page.other.path}>{page.other.label}</Link>
        </p>
      </div>
    </AuthLayout>
  );
}

/** Names the page in the browser tab while it is open. */
function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · Nabble Suite`;
    return () => {
      document.title = "Nabble Suite";
    };
  }, [title]);
}
